<template>
  <div class="program-form-field">
    <div
      class="form-group"
      :class="{ 'has-error': showError, 'is-prefilled': hasPrefilledValue }"
    >
      <label class="control-label"
        >{{ questionNo }}. {{ question.text
        }}<span v-if="question.required"> *</span></label
      >

      <div v-if="showError">
        <p
          v-for="error in errors"
          :key="error"
          class="validation-text text-danger"
        >
          {{ error }}
        </p>
      </div>

      <div class="answer-field">
        <div v-if="question.type === 'SINGLE_CHOICES'">
          <MultipleChoicesDropdown
            v-if="isDropdown"
            class="seamless overview"
            title="option"
            :choices="question.choices"
            :single-choice="true"
            :value="value"
            :search-enabled="question.choices.length > 8"
            @change="onChoiceSelected"
          />
          <div v-if="!isDropdown">
            <div
              v-for="choice in question.choices"
              :key="choice.value"
              class="radio"
            >
              <label>
                <input
                  v-model="value"
                  :disabled="!editable"
                  type="radio"
                  :value="choice.value"
                />
                <span>{{ choice.label }}</span>
                <span v-if="choice.hint" class="text-muted"
                  >({{ choice.hint }})</span
                >
              </label>
            </div>
          </div>
        </div>

        <div v-if="question.type === 'MULTIPLE_CHOICES'">
          <MultipleChoicesDropdown
            v-if="isDropdown"
            class="seamless overview"
            title="option"
            :choices="question.choices"
            :value="selected"
            :search-enabled="question.choices.length > 8"
            @change="onChoicesSelected"
          />
          <div v-if="!isDropdown">
            <div
              v-for="(choice, idx) in question.choices"
              :key="`Checkbox_${choice.value}`"
              class="checkbox"
            >
              <label>
                <input
                  v-model="selected"
                  :disabled="!editable"
                  type="checkbox"
                  :id="`CheckboxMulti_${idx}`"
                  :value="choice.value"
                />
                <span>{{ choice.label }}</span>
              </label>
            </div>
          </div>
        </div>

        <textarea
          v-if="question.type === 'LONG_TEXT' || question.type === 'TEXT'"
          v-model="value"
          v-autosize
          :disabled="!editable"
          rows="1"
          class="form-control overview-input"
          placeholder="Type your answer here"
        ></textarea>

        <input
          v-if="question.type === 'NUMBER'"
          v-model="value"
          :disabled="!editable"
          class="form-control overview-input"
        />

        <img
          v-if="question.type === 'IMAGE' && value"
          class="image-answer"
          :src="value"
        />
        <Dropzone
          v-if="question.type === 'IMAGE' && editable"
          title="Upload logo"
          @upload-done="onImageUploaded"
        />

        <span v-if="question.type === 'ATTACHMENT' && (!data || !data.url)"
          >-</span
        >
        <button
          v-if="question.type === 'ATTACHMENT' && data.url"
          class="btn btn-default margin-bottom-10 attachment-btn"
          @click="downloadFile(data.url, data.filename)"
        >
          <i class="ion ion-paperclip"></i>
          <span class="attachment-label">{{ data.filename }}</span>
          <el-tooltip content="Click to delete this attachment" placement="top">
            <i
              class="ion ion-close remove-attachment-btn"
              @click.prevent="onDeleteAttachmentClicked()"
            ></i>
          </el-tooltip>
        </button>
        <Dropzone
          v-if="question.type === 'ATTACHMENT' && editable"
          title="Upload attachment"
          @upload-done="onAttachmentUploaded"
        />

        <YearMonthPicker
          v-if="question.type === 'YEAR_MONTH'"
          v-model="yearMonth"
          placeholder="Pick a month"
        />

        <div v-if="question.type === 'AGREEMENT'" class="checkbox">
          <label>
            <input v-model="value" type="checkbox" :disabled="!editable" />
            <span>Agree</span>
            <span v-if="question.url"
              >(
              <a :href="normalizeLink(question.url)" target="_new">Source</a>
              )</span
            >
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import autosize from '@/directives/autosize'
import MultipleChoicesDropdown from '@/components/MultipleChoicesDropdown'
import { normalizeLink } from '@/utils'
import http from '@/services/http'

export default {
  components: { MultipleChoicesDropdown },
  directives: { autosize },
  props: {
    editable: { type: Boolean, default: true },
    questionNo: { type: Number, default: null },
    question: { type: Object, default: () => ({}) },
    showInvalidState: { type: Boolean, default: false },
    prefilledWith: { type: Object, default: () => ({ answer: {} }) },
  },
  data() {
    return {
      value: '',
      data: getEmptyAttachmentData(),
      selected: [],
      errors: [],
    }
  },
  watch: {
    value() {
      this.emitChange()
    },
    selected() {
      this.emitChange()
    },
    data() {
      this.emitChange()
    },
  },
  computed: {
    hasPrefilledValue() {
      return this.value === this.prefilledWith.answer.value
    },
    isDropdown() {
      return this.question.widget === 'DROPDOWN'
    },
    isValid() {
      return this.errors.length <= 0
    },
    hasData() {
      return (
        !!this.value ||
        (this.selected || []).length > 0 ||
        !!(this.data || {}).url
      )
    },
    showError() {
      return this.showInvalidState && !this.isValid
    },
    yearMonth: {
      get() {
        if (this.value) {
          return moment(this.value).format('MMM Y')
        } else {
          return ''
        }
      },
      set(val) {
        if (val) {
          this.value = moment(val, 'MMM Y').format('Y-MM-DD')
        } else {
          this.value = null
        }
      },
    },
  },
  methods: {
    async emitChange() {
      const errors = await this.checkErrors()
      if (errors.length > 0) return
      this.$emit('change', {
        question: this.question,
        answer: this.getAnswerData(),
        isValid: this.isValid,
      })
    },
    checkErrors() {
      const errors = []
      if (this.question.required) {
        if (!this.hasData) {
          let msg = 'This field is required.'
          if (this.question.type === 'MULTIPLE_CHOICES') {
            msg = 'You have to choose at least one option'
          }
          errors.push(msg)
        }
      }
      if (this.question.type === 'NUMBER') {
        const isNumberValid = /^\d*$/.test(this.value)
        const isEmpty = !this.value
        if ((!isEmpty || this.question.required) && !isNumberValid) {
          errors.push('Must be a number.')
        }
      }

      this.errors = errors
      return errors
    },
    getAnswerData() {
      const answer = {
        value: this.value,
        selected: (this.selected || []).map((o) => o),
      }
      if (this.question.type === 'ATTACHMENT') {
        const { url, filename, type } = this.data
        if (url && filename && type) {
          answer.data = this.data
        }
      }
      if (this.question.type === 'SINGLE_CHOICES') {
        if (!answer.value && !this.question.required) {
          delete answer.value
        }
      }
      if (this.question.type === 'YEAR_MONTH') {
        if (!answer.value && !this.question.required) {
          delete answer.value
        }
      }
      if (this.question.type === 'NUMBER') {
        if (!answer.value && !this.question.required) {
          answer.value = null
        }
      }
      return answer
    },
    setAnswer(answer) {
      this.value = answer.value
      this.selected = answer.selected || []
      if (answer.data) {
        this.data = answer.data
      }
    },
    onChoiceSelected(choice) {
      this.value = choice.value
    },
    onChoicesSelected(choices) {
      this.selected = choices.map((c) => c.value)
    },
    onImageUploaded({ file, url }) {
      this.value = url
    },
    onAttachmentUploaded({ file, url }) {
      if (!file) {
        return
      }
      this.data = {
        url,
        filename: file.name,
        type: file.type,
        size: file.size,
      }
    },
    async onDeleteAttachmentClicked() {
      const title = 'Confirmation'
      const message = 'Are you sure you want to remove this attachment?'
      try {
        await this.$confirm(message, title)
        this.clearAttachment()
        this.$message({
          type: 'success',
          message: 'Attachment removed',
        })
      } catch (err) {
        // dialog closes without confirmation
      }
    },
    clearAttachment() {
      this.data = getEmptyAttachmentData()
    },
    normalizeLink,
    downloadFile(url, filename) {
      url = this.$filters.cld(url)
      http.downloadFile(url, filename)
    },
  },
}

function getEmptyAttachmentData() {
  return {
    url: null,
    filename: null,
    type: null,
    size: null,
  }
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.program-form-field {
  .is-prefilled {
    // border: solid 2px $blue;
  }

  .validation-text {
    font-size: 12px;
    font-weight: 300;
  }

  .answer-field {
    margin: 20px 10px;
  }

  .form-control[disabled] {
    background-color: transparent;
    cursor: text;
  }

  .attachment-btn {
    .remove-attachment-btn {
      display: inline-block;
      margin-left: 10px;

      &:hover {
        color: $danger;
      }
    }
  }
}
</style>
